import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { BrandProvider } from "@/lib/BrandContext";
import RequireAuth from "@/lib/RequireAuth";
import ErrorBoundary from "@/components/ErrorBoundary";
import AppLoading from "@/components/AppLoading";
import ScrollManager from "@/components/ScrollManager";

import Home from "@/pages/public/Home";
import Features from "@/pages/public/Features";
import Pricing from "@/pages/public/Pricing";
import FAQ from "@/pages/public/FAQ";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import AcceptInvite from "@/pages/auth/AcceptInvite";

import CoachDashboard from "@/pages/coach/Dashboard";
import Teams from "@/pages/coach/Teams";
import TeamDetail from "@/pages/coach/TeamDetail";
import Charges from "@/pages/coach/Charges";
import CreateCharge from "@/pages/coach/CreateCharge";
import ChargeDetail from "@/pages/coach/ChargeDetail";
import PlayerLedger from "@/pages/coach/PlayerLedger";
import Payments from "@/pages/coach/Payments";
import Settings from "@/pages/coach/Settings";

import ParentDashboard from "@/pages/parent/Dashboard";
import Owed from "@/pages/parent/Owed";
import MarkAsPaid from "@/pages/parent/MarkAsPaid";
import History from "@/pages/parent/History";

import AdminDashboard from "@/pages/admin/Dashboard";

function RoleRedirect() {
  const { user, loading } = useAuth();
  // Show the branded loading state while auth is resolving — never a
  // blank white screen. This was the regression that hit users during
  // the Industry Profiles rollout.
  if (loading || user === null) return <AppLoading testid="role-redirect-loading" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "coach") return <Navigate to="/coach/dashboard" replace />;
  if (user.role === "parent") return <Navigate to="/parent/dashboard" replace />;
  if (user.role === "platform_admin") return <Navigate to="/admin/dashboard" replace />;
  return <Navigate to="/" replace />;
}

/**
 * IMPORTANT provider ordering
 * ---------------------------
 * <AuthProvider> MUST wrap <BrandProvider>. BrandProvider gates its
 * /api/orgs/mine probe on the auth state and would otherwise fire it for
 * guest visitors, triggering the global 401 handler and hard-redirecting
 * every landing-page visitor to /login (blank-white flash).
 */
export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <BrandProvider>
            <ScrollManager />
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />

              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/invite/:code" element={<AcceptInvite />} />

              <Route path="/app" element={<RoleRedirect />} />

              <Route path="/coach/dashboard" element={<RequireAuth role="coach"><CoachDashboard /></RequireAuth>} />
              <Route path="/coach/teams" element={<RequireAuth role="coach"><Teams /></RequireAuth>} />
              <Route path="/coach/teams/:id" element={<RequireAuth role="coach"><TeamDetail /></RequireAuth>} />
              <Route path="/coach/charges" element={<RequireAuth role="coach"><Charges /></RequireAuth>} />
              <Route path="/coach/charges/new" element={<RequireAuth role="coach"><CreateCharge /></RequireAuth>} />
              <Route path="/coach/charges/:id" element={<RequireAuth role="coach"><ChargeDetail /></RequireAuth>} />
              <Route path="/coach/players/:playerId" element={<RequireAuth role="coach"><PlayerLedger /></RequireAuth>} />
              <Route path="/coach/payments" element={<RequireAuth role="coach"><Payments /></RequireAuth>} />
              {/* Legacy routes — Pending + Reports are now part of the unified Payments page. */}
              <Route path="/coach/pending" element={<Navigate to="/coach/payments?status=submitted" replace />} />
              <Route path="/coach/reports" element={<Navigate to="/coach/payments" replace />} />
              {/* Reminders is now an inline action on Charges, Payments, ChargeDetail. The
                  standalone page redirects to Charges (the most natural "send a reminder" surface). */}
              <Route path="/coach/reminders" element={<Navigate to="/coach/charges" replace />} />
              <Route path="/coach/settings" element={<RequireAuth role="coach"><Settings /></RequireAuth>} />

              <Route path="/parent/dashboard" element={<RequireAuth role="parent"><ParentDashboard /></RequireAuth>} />
              <Route path="/parent/owed" element={<RequireAuth role="parent"><Owed /></RequireAuth>} />
              <Route path="/parent/owed/:id/pay" element={<RequireAuth role="parent"><MarkAsPaid /></RequireAuth>} />
              <Route path="/parent/history" element={<RequireAuth role="parent"><History /></RequireAuth>} />

              <Route path="/admin/dashboard" element={<RequireAuth role="platform_admin"><AdminDashboard /></RequireAuth>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrandProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
